/** Thin wrappers over the browser APIs the player leans on. Every one of them is optional:
 *  iOS in particular refuses vibration outright and only allows notifications from an
 *  installed PWA, so nothing here is allowed to throw into the render path. */

let AC: AudioContext | null = null;

/**
 * The one AudioContext: the beeps and the voice clips (`lib/voice.ts`) share it, so a clip plays
 * under the same user activation the first beep unlocked. Null where there is no Web Audio.
 */
export function audioContext(): AudioContext | null {
  try {
    const Ctor =
      window.AudioContext ||
      (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctor) return null;
    AC = AC || new Ctor();
    if (AC.state === "suspended") void AC.resume();
    return AC;
  } catch {
    return null;
  }
}

export function beep(freq: number, ms?: number, vol?: number, enabled = true): void {
  if (!enabled) return;
  try {
    const ctx = audioContext();
    if (!ctx) return;
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.frequency.value = freq;
    o.type = "sine";
    g.gain.value = vol ?? 0.15;
    g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + (ms || 120) / 1000);
    o.connect(g);
    g.connect(ctx.destination);
    o.start();
    o.stop(ctx.currentTime + (ms || 120) / 1000);
  } catch {
    /* audio blocked until a user gesture, or unsupported */
  }
}

export function buzz(pattern?: number | number[], enabled = true): void {
  if (!enabled || !navigator.vibrate) return;
  try {
    navigator.vibrate(pattern ?? 120);
  } catch {
    /* ignore */
  }
}

let wakeLock: WakeLockSentinel | null = null;

/** Keeps the screen on during a timed session. */
export async function setWakeLock(on: boolean): Promise<void> {
  try {
    if (on && "wakeLock" in navigator) {
      if (!wakeLock) {
        wakeLock = await navigator.wakeLock.request("screen");
        wakeLock.addEventListener("release", () => {
          wakeLock = null;
        });
      }
    } else if (!on && wakeLock) {
      await wakeLock.release();
      wakeLock = null;
    }
  } catch {
    /* denied or unsupported — the timer still runs, the screen just may sleep */
  }
}

/* ---------- notifications ---------- */

let swReg: ServiceWorkerRegistration | null = null;

export function setSwRegistration(r: ServiceWorkerRegistration | null): void {
  swReg = r;
}

/** The worker's registration, for the push subscription (lib/push.ts). */
export function swRegistration(): ServiceWorkerRegistration | null {
  return swReg;
}

export function notificationPermission(): NotificationPermission | "unsupported" {
  if (typeof Notification === "undefined") return "unsupported";
  return Notification.permission;
}

export async function askNotificationPermission(): Promise<NotificationPermission | "unsupported"> {
  if (typeof Notification === "undefined") return "unsupported";
  try {
    return await Notification.requestPermission();
  } catch {
    return Notification.permission;
  }
}

export function canNotify(): boolean {
  return notificationPermission() === "granted";
}

/** Hands the service worker a one-shot reminder. Best effort: the OS may drop long timers,
 *  which is why callers re-schedule on every app launch. */
export function scheduleNotification(id: string, at: number, title: string, body: string): void {
  if (!canNotify()) return;
  try {
    swReg?.active?.postMessage({ type: "schedule", id, at, title, body });
  } catch {
    /* ignore */
  }
}

export function cancelNotification(id: string): void {
  try {
    swReg?.active?.postMessage({ type: "cancel", id });
  } catch {
    /* ignore */
  }
}

/** The dot on the installed app icon: cleared once today's session is done. */
export function setAppBadge(n: number): void {
  try {
    const nav = navigator as Navigator & {
      setAppBadge?: (n?: number) => Promise<void>;
      clearAppBadge?: () => Promise<void>;
    };
    if (n && nav.setAppBadge) void nav.setAppBadge(n);
    else if (!n && nav.clearAppBadge) void nav.clearAppBadge();
  } catch {
    /* ignore */
  }
}

/* ---------- display mode ---------- */

export const isIOS = (): boolean => /iphone|ipad|ipod/i.test(navigator.userAgent);

export const isStandalone = (): boolean =>
  matchMedia("(display-mode: standalone)").matches ||
  (navigator as Navigator & { standalone?: boolean }).standalone === true;

export const prefersReducedMotion = (): boolean =>
  matchMedia("(prefers-reduced-motion: reduce)").matches;

/** Crude on purpose: this only decides whether to offer the Android companion app. */
export function isAndroid(): boolean {
  return /android/i.test(navigator.userAgent);
}
